import { FC, FormEvent } from 'react';
import { ArrowForwardRounded, ShieldRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import './TeamCreateCard.scss';
import logoSrc from '../../../assets/logo/logo.png';
import { TEAM_NAME_MAX_LENGTH } from '../constants';

interface TeamCreateCardProps {
  inputId: string;
  teamName: string;
  createError: string | null;
  isCreateDisabled: boolean;
  isCreatingTeam: boolean;
  onTeamNameChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const TeamCreateCard: FC<TeamCreateCardProps> = ({
  inputId,
  teamName,
  createError,
  isCreateDisabled,
  isCreatingTeam,
  onTeamNameChange,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <div className="team-create-card team-card team-card--create">
      <img src={logoSrc} alt="" className="team-page__create-logo" />
      <h1 className="team-page__title team-page__title--create">{t('team.create.title')}</h1>
      <Typography
        variant="body2"
        component="p"
        className="team-page__subtitle team-page__subtitle--create"
      >
        {t('team.create.description')}
      </Typography>

      <form className="team-page__form" onSubmit={onSubmit}>
        <div className="team-page__field">
          <label htmlFor={inputId} className="team-page__label">
            <Typography variant="subtitle1" component="span">
              {t('team.common.teamName')}
            </Typography>
          </label>
          <input
            id={inputId}
            className="team-page__input"
            value={teamName}
            onChange={(event) => onTeamNameChange(event.target.value)}
            placeholder={t('team.common.teamNamePlaceholder')}
            maxLength={TEAM_NAME_MAX_LENGTH}
            autoComplete="off"
            autoFocus
          />
          <div className="team-page__helper-row">
            <Typography variant="subtitle1" component="span" className="team-page__helper-text">
              {teamName.trim().length}/80
            </Typography>
          </div>
        </div>

        <AnimatePresence>
          {createError && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              <Typography variant="body2" component="p" className="team-page__error">
                {createError}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="team-page__owner-card">
          <ShieldRounded className="team-page__owner-card-icon" />
          <div className="team-page__owner-card-content">
            <Typography variant="body1" component="p" className="team-page__owner-card-title">
              {t('team.create.ownerTitle')}
            </Typography>
            <Typography variant="body2" component="p" className="team-page__owner-card-description">
              {t('team.create.ownerDescription')}
            </Typography>
          </div>
        </div>

        <div className="team-page__actions">
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            className="team-page__button team-page__button--primary"
            disabled={isCreateDisabled}
            loading={isCreatingTeam}
          >
            <Typography variant="body1" component="span">
              {t('team.create.cta')}
            </Typography>
            <ArrowForwardRounded className="team-page__button-icon team-page__button-icon--end" />
          </LoadingButton>
        </div>
      </form>
    </div>
  );
};

export default TeamCreateCard;
